import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { YaEvent, YaReadyEvent } from 'angular8-yandex-maps';

import { PlacemarkType, BoundsType, PointType } from './types';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnChanges, OnDestroy {
  @Input() placemarks: PlacemarkType[] = [];
  @Input() center?: PointType;
  @Input() bounds?: BoundsType;
  @Output() boundsChange = new EventEmitter<BoundsType>();

  private map?: ymaps.Map;

  mapState: ymaps.IMapState = {
    zoom: 12.82,
    controls: ['fullscreenControl', 'geolocationControl', 'zoomControl'],
  };

  mapOptions: ymaps.IMapOptions = {
    suppressMapOpenBlock: true,
    copyrightLogoVisible: false,
  };

  clustererOptions: ymaps.IClustererOptions = {
    preset: 'islands#blueClusterIcons',
    clusterBalloonPanelMaxMapArea: 0,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['center']) {
      if (this.center) {
        this.mapState = {
          ...this.mapState,
          center: [this.center.latitude, this.center.longitude],
        };
      }
    }

    if (changes['placemarks']) {
      this.initPlacemarks();
    }
  }

  ngOnDestroy(): void {
    this.map?.container.exitFullscreen();
  }

  sizeChange(event: YaEvent<ymaps.Map>) {
    const map = event.target;
    this.fitAllPlacemarksOnMap(map);
  }

  actionEnd(event: YaEvent<ymaps.Map>) {
    const map = event.target;
    this.emitBounds(map);
  }

  ready(event: YaReadyEvent<ymaps.Map>) {
    this.initPlacemarks();

    const map = event.target;
    this.map = map;
    this.emitBounds(map);
    this.fitAllPlacemarksOnMap(map);
  }

  private fitAllPlacemarksOnMap(map: ymaps.Map) {
    if (this.center) return;

    const bounds = map.geoObjects.getBounds();
    if (bounds) {
      map.setBounds(bounds, {
        checkZoomRange: true,
        zoomMargin: [30, 30, 30, 30],
      });
    }
  }

  private emitBounds(map: ymaps.Map) {
    const corners = map.getBounds();
    const bounds: BoundsType | null = corners
      ? {
          lowerLeftCorner: {
            latitude: corners[0][0],
            longitude: corners[0][1],
          },
          upperRightCorner: {
            latitude: corners[1][0],
            longitude: corners[1][1],
          },
        }
      : null;
    if (bounds) {
      this.boundsChange.emit(bounds);
    }
  }

  private initPlacemarks() {
    this.placemarks.map(placemark => {
      // INFO: проверка window.ymaps добавлена для исправления ошибки в консоли #12898
      if (placemark.balloonContentLayout && window.ymaps) {
        const balloonContentLayout = ymaps.templateLayoutFactory.createClass(placemark.balloonContentLayout.template, {
          ...placemark.balloonContentLayout?.overrides,
          build: function () {
            balloonContentLayout.superclass.build.call(this);
            placemark.balloonContentLayout?.overrides.build();
          },
          clear: function () {
            placemark.balloonContentLayout?.overrides.clear();
            balloonContentLayout.superclass.clear.call(this);
          },
        });

        // this.clustererOptions.clusterBalloonItemContentLayout = balloonContentLayout TODO: проверить на 3 версии
        ymaps.layout.storage.add('custom#defaultBCLayout', balloonContentLayout);
        placemark.options.balloonContentLayout = 'custom#defaultBCLayout';
      }
    });

    this.clustererOptions.clusterBalloonItemContentLayout = 'custom#defaultBCLayout';
  }
}
